'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'

export default function BreathingTechniquesPage() {
  const { theme } = useTheme()

  return (
    <div className={`min-h-screen py-6 xxs:py-3 px-4 xxs:px-2 ${
      theme === 'light'
        ? 'bg-gradient-to-b from-blue-50 to-white'
        : 'bg-gradient-to-b from-gray-900 to-gray-800'
    }`}>
      <div className="max-w-4xl mx-auto">
        <Link href="/guide" className={`hover:underline mb-3 xxs:mb-2 inline-block text-sm xxs:text-xs ${
          theme === 'light' ? 'text-blue-600' : 'text-blue-400'
        }`}>
          ← 입문 가이드 목록
        </Link>

        <article className={`rounded-lg shadow-xl p-6 xxs:p-3 ${
          theme === 'light' ? 'bg-white' : 'bg-gray-800'
        }`}>
          <h1 className={`text-2xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${
            theme === 'light' ? 'text-gray-900' : 'text-white'
          }`}>프리다이빙 호흡법 가이드</h1>

          <div className="max-w-none">
            <p className={`text-sm xxs:text-xs mb-4 xxs:mb-3 ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              효과적인 호흡 테크닉으로 더 오래, 더 깊이 다이빙하세요.
            </p>

            <div className={`p-4 xxs:p-3 rounded-lg border-l-4 border-red-500 mb-4 xxs:mb-3 ${
              theme === 'light' ? 'bg-red-50' : 'bg-red-900/20'
            }`}>
              <h3 className={`font-bold text-base xxs:text-sm ${
                theme === 'light' ? 'text-red-900' : 'text-red-300'
              }`}>⚠️ 중요 경고</h3>
              <p className={`text-xs xxs:text-[10px] mt-2 xxs:mt-1.5 ${
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                과호흡(Hyperventilation)은 절대 금지입니다! 블랙아웃 위험이 증가합니다.
              </p>
            </div>

            <h2 className={`text-base xxs:text-sm font-bold mt-6 xxs:mt-4 mb-3 xxs:mb-2 ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>다이빙 전 호흡법</h2>

            <div className="space-y-3 xxs:space-y-2">
              <div className={`p-4 xxs:p-3 rounded-lg ${
                theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'
              }`}>
                <h3 className={`font-bold text-sm xxs:text-xs mb-2 xxs:mb-1.5 ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>1. 이완 호흡 (Relaxation Breathing)</h3>
                <p className={`text-xs xxs:text-[10px] mb-2 xxs:mb-1.5 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  다이빙 5-10분 전부터 시작하는 준비 호흡
                </p>
                <ul className={`text-xs xxs:text-[10px] space-y-1 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <li>• 깊고 천천히 들이마시기 (4초)</li>
                  <li>• 잠시 멈춤 (2초)</li>
                  <li>• 천천히 내쉬기 (6초)</li>
                  <li>• 심박수를 낮추고 몸을 이완시킵니다</li>
                </ul>
              </div>

              <div className={`p-4 xxs:p-3 rounded-lg ${
                theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'
              }`}>
                <h3 className={`font-bold text-sm xxs:text-xs mb-2 xxs:mb-1.5 ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>2. 복식 호흡 (Diaphragmatic Breathing)</h3>
                <p className={`text-xs xxs:text-[10px] mb-2 xxs:mb-1.5 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  횡격막을 이용한 효율적인 호흡법
                </p>
                <ul className={`text-xs xxs:text-[10px] space-y-1 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <li>• 배를 부풀리며 들이마시기</li>
                  <li>• 가슴이 아닌 배가 움직여야 함</li>
                  <li>• 폐활량을 최대로 활용</li>
                  <li>• 산소 흡수율 증가</li>
                </ul>
              </div>

              <div className={`p-4 xxs:p-3 rounded-lg ${
                theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'
              }`}>
                <h3 className={`font-bold text-sm xxs:text-xs mb-2 xxs:mb-1.5 ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>3. 풀 브레스 (Full Breath)</h3>
                <p className={`text-xs xxs:text-[10px] mb-2 xxs:mb-1.5 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  다이빙 직전 마지막 호흡
                </p>
                <ul className={`text-xs xxs:text-[10px] space-y-1 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <li>• 배 → 가슴 → 쇄골 순으로 채우기</li>
                  <li>• 80-90% 정도만 채웁니다 (100% X)</li>
                  <li>• 너무 가득 차면 오히려 불편함</li>
                  <li>• 편안한 상태 유지가 중요</li>
                </ul>
              </div>
            </div>

            <h2 className={`text-base xxs:text-sm font-bold mt-6 xxs:mt-4 mb-3 xxs:mb-2 ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>호흡 훈련법</h2>

            <div className={`p-4 xxs:p-3 rounded-lg ${
              theme === 'light' ? 'bg-green-50' : 'bg-green-900/20'
            }`}>
              <h3 className={`font-bold text-sm xxs:text-xs mb-3 xxs:mb-2 ${
                theme === 'light' ? 'text-gray-900' : 'text-white'
              }`}>일상 속 호흡 연습</h3>
              <div className={`space-y-2 xxs:space-y-1.5 text-xs xxs:text-[10px] ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                <div>
                  <p className="font-semibold">Morning Routine (아침 5분)</p>
                  <p>침대에서 일어나기 전 복식 호흡 연습</p>
                </div>
                <div>
                  <p className="font-semibold">Breath Hold Training (주 2-3회)</p>
                  <p>편안한 자세로 숨 참기 연습 (절대 무리하지 말 것)</p>
                </div>
                <div>
                  <p className="font-semibold">CO2 Table (격주)</p>
                  <p>이산화탄소 내성 훈련 (전문가 지도 권장)</p>
                </div>
              </div>
            </div>

            <div className={`mt-6 xxs:mt-4 p-4 xxs:p-3 rounded-lg ${
              theme === 'light' ? 'bg-yellow-50' : 'bg-yellow-900/20'
            }`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 xxs:mb-1.5 ${
                theme === 'light' ? 'text-gray-900' : 'text-white'
              }`}>💡 호흡법 팁</h3>
              <ul className={`text-xs xxs:text-[10px] space-y-1.5 xxs:space-y-1 ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                <li>• 긴장하면 산소 소비가 증가합니다 - 항상 릴랙스!</li>
                <li>• 물속에서는 움직임을 최소화하세요</li>
                <li>• 호흡 훈련은 안전한 환경에서만 하세요</li>
                <li>• 혼자 숨참기 훈련은 절대 금지</li>
                <li>• 정기적인 연습이 실력 향상의 핵심입니다</li>
              </ul>
            </div>

            <div className={`mt-6 xxs:mt-4 p-4 xxs:p-3 rounded-lg ${
              theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'
            }`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 xxs:mb-1.5 ${
                theme === 'light' ? 'text-gray-900' : 'text-white'
              }`}>함께 연습할 버디 찾기</h3>
              <p className={`text-xs xxs:text-[10px] mb-3 xxs:mb-2 ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                안전한 호흡 훈련을 위해 함께 연습할 버디를 찾아보세요!
              </p>
              <Link
                href="/matching/new"
                className="inline-block px-4 xxs:px-3 py-2 xxs:py-1.5 text-xs xxs:text-[10px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                버디 매칭 신청하기
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
